// @flow
import useIsAdmin from '@streaming-projects/useIsAdmin';
import {
  useCreateCommitMetricDiscountMutation,
  useDeleteCommitMetricDiscountGroupMutation,
  useDeleteCommitMetricDiscountMutation,
  useUpdateCommitGlobalDiscountMutation,
  useUpdateCommitMetricDiscountMutation,
} from '@streaming-projects/service-definitions/streamingProjectsApi';
import { SPMLDMutationsContext } from '@streaming-projects/orgs/org-top-level/sp-custom-discounts/contexts/SPMLDMutationsContext';
import { PaddedAndRaisedSegment } from '@presentational/PaddedAndRaisedSegment';
import React, { useContext } from 'react';
import { CommitGlobalDiscountContainer } from '@streaming-projects/user-commit-details/commit-custom-discounts/global-discount/CommitGlobalDiscountContainer';
import { CommitMetricLevelDiscountLabelContainer } from '@streaming-projects/user-commit-details/commit-custom-discounts/metrics-level-configuration/CommitMetricLevelDiscountLabelContainer';
import { CommitMetricsLevelDiscountsTopLevelContainer } from '@streaming-projects/user-commit-details/commit-custom-discounts/metrics-level-configuration/CommitMetricsLevelDiscountsTopLevelContainer';
import { SP_PRICING_STATUS_STATES } from '@src/constants';
import { NegativeMessageContainer } from '@presentational/messages/NegativeMessageContainer';
import { StyledHeader } from '@src/common-utils/styledComponents';
import { SPUserCommitPricingStatusContext } from '@streaming-projects/user-commit-details/contexts/SPUserCommitPricingStatusContext';
import { isAnythingEditedBesidesGivenFormBasedOnGivenConfig } from '@streaming-projects/utils';
import { SP_USER_COMMIT_PAGE_CONFIG } from '@streaming-projects/user-commit-details/config';
import { FORM_NAMES } from '@streaming-projects/enums';
import { useFormikContext } from 'formik';

// todo::SP For now, commit MLD is a copy paste of Org MLD. After commit MLD stabilizes, merge them using configs
export const SPUserCommitDiscountsTopLevelContainer = () => {
  const { values, initialValues } = useFormikContext();
  const shouldFormBeDisabled = isAnythingEditedBesidesGivenFormBasedOnGivenConfig(
    initialValues,
    values,
    SP_USER_COMMIT_PAGE_CONFIG,
    FORM_NAMES.CUSTOM_DISCOUNTS.CUSTOM_DISCOUNTS_FORM
  );

  const { data: pricingStatusData, isPricingStatusLoading } = useContext(
    SPUserCommitPricingStatusContext
  );
  const isUserAdmin = useIsAdmin();

  const readOnlyModeToUse = !isUserAdmin || shouldFormBeDisabled;
  const mutations = {
    globalDiscountUpdateMutation: useUpdateCommitGlobalDiscountMutation(),
    metricDiscountCreateMutation: useCreateCommitMetricDiscountMutation(),
    metricDiscountUpdateMutation: useUpdateCommitMetricDiscountMutation(),
    metricDiscountDeleteMutation: useDeleteCommitMetricDiscountMutation(),
    metricDiscountGroupDeleteMutation: useDeleteCommitMetricDiscountGroupMutation(),
  };
  if (isPricingStatusLoading) {
    return (
      <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
        <StyledHeader>Loading pricing status...</StyledHeader>
      </PaddedAndRaisedSegment>
    );
  }

  const pricingStatus = pricingStatusData?.status;

  // For all other pricing statuses, show the same content as commit summary section but without recalc button
  if (pricingStatus === SP_PRICING_STATUS_STATES.FAILED) {
    return (
      <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
        <NegativeMessageContainer
          body={
            <p>
              Please try again by clicking the Re-Calculate Now button in the Commit Summary
              section. If it persists, please contact #cloud-commitment-estimator channel
            </p>
          }
          header="An Unexpected Pricing Error Occurred"
        />
      </PaddedAndRaisedSegment>
    );
  }

  if (pricingStatus === SP_PRICING_STATUS_STATES.PENDING) {
    return (
      <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
        <StyledHeader style={{ color: 'red' }}>
          You have made some edits! Please click on Re-Calculate Now in the Commit Summary section
          to fetch latest data!
        </StyledHeader>
      </PaddedAndRaisedSegment>
    );
  }

  if (
    pricingStatus === SP_PRICING_STATUS_STATES.IN_PROGRESS ||
    pricingStatus === SP_PRICING_STATUS_STATES.QUEUED
  ) {
    return (
      <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
        <StyledHeader style={{ color: 'red' }}>
          Note: Repricing is still happening! Please wait while the reprice finishes. You can
          refresh the page periodically to get the latest status.
        </StyledHeader>
      </PaddedAndRaisedSegment>
    );
  }

  if (pricingStatus === SP_PRICING_STATUS_STATES.SUCCESS) {
    return (
      <SPMLDMutationsContext.Provider value={mutations}>
        <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
          <CommitGlobalDiscountContainer readOnlyMode={shouldFormBeDisabled} />
          <div style={{ marginLeft: '1.5rem', marginTop: '2rem', marginBottom: '2rem' }}>
            {/* { todo::SP reuse the styles from styles component} */}
            <CommitMetricLevelDiscountLabelContainer />
          </div>
          <CommitMetricsLevelDiscountsTopLevelContainer readOnlyMode={readOnlyModeToUse} />
        </PaddedAndRaisedSegment>
      </SPMLDMutationsContext.Provider>
    );
  }

  // Default case - show loading or empty state
  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <StyledHeader>Loading pricing status...</StyledHeader>
    </PaddedAndRaisedSegment>
  );
};
